<template>
  <layout-pondlogs v-loading="loading" class="pond-details" headerType="custom">
    <template v-if="pondId && !!selectedPondDetails">
      <div slot="header">
        <ponds-select-with-details :key="pondId"></ponds-select-with-details>
      </div>
      <er-tabs
        :value="computedComponentName"
        @tab-click="handleTabClick"
        slot="pondlogs-default-layout-main"
      >
        <el-tab-pane
          v-for="(tab, key) in fitlerTabItems"
          :name="tab.name"
          :key="key"
        >
          <span class="pond__details__tabs" slot="label">{{ tab.label }}</span>
        </el-tab-pane>

        <portal to="rightdestination">
          <moonPhase />
        </portal>
        <component
          :is="computedComponentName"
          :key="`${pondId} ${getCultureName}`"
          :tabData="fitlerTabItems[computedComponentName]"
        ></component>
      </er-tabs>
    </template>
    <div v-else-if="!selectedPondDetails && !loading" slot="header">
      <el-alert :closable="false">
        <center v-loading="loading">
          {{ $t("Comn_no_ponds_data_found") }}
        </center>
      </el-alert>
    </div>
  </layout-pondlogs>
</template>

<script>
import pondCharts from "@/components/culture/pondCharts";
import pondsSelectWithDetails from "@/components/pond/pondsSelectWithDetails";
import pondInputTab from "@/components/culture/pondInputTab";
import pondLabTestsTab from "@/components/culture/pondLabTestsTab";
import pondFeedTab from "@/components/culture/pondFeedTab";
import pondABWTab from "@/components/culture/pondABWTab";
import moonPhase from "@/components/pond/moonPhase";
import errorHandlerMixin from "@/mixins/errorHandlerMixin";
import { mapGetters, mapActions } from "vuex";

import pondDetailsMod from "@/store/components/pondDetails";
import pondmotherMod from "@/store/components/pondmother";
import resourceMod from "@/store/components/resource";
import medicinesMod from "@/store/components/medicines";
import labTestsMod from "@/store/components/labTests";

import {
  permissionsToStringMap as permissions,
  isValidTabName,
  getFirstTabBasedOnPermissions
} from "@/middleware/pageAccessManager";
const tabToPermission = {
  pondCharts: {
    name: "pondCharts",
    keyToValidateAccess: "PERMISSIONS",
    PERMISSIONS: [permissions.MANAGE_CULTURE, permissions.MANAGE_SCHEDULES]
  },
  pondFeedTab: {
    name: "pondFeedTab",
    keyToValidateAccess: "PERMISSIONS",
    PERMISSIONS: [permissions.MANAGE_CULTURE, permissions.MANAGE_SCHEDULES]
  },
  pondInputTab: {
    name: "pondInputTab",
    keyToValidateAccess: "PERMISSIONS",
    PERMISSIONS: [permissions.MANAGE_MEDICINE]
  },
  pondABWTab: {
    name: "pondABWTab",
    keyToValidateAccess: "PERMISSIONS",
    PERMISSIONS: [permissions.MANAGE_ABW]
  },
  pondLabTestsTab: {
    name: "pondLabTestsTab",
    keyToValidateAccess: "PERMISSIONS",
    PERMISSIONS: [permissions.MANAGE_LAB_TESTS]
  }
};

export default {
  mixins: [errorHandlerMixin],
  name: "PondDetails",
  components: {
    pondCharts,
    pondsSelectWithDetails,
    pondFeedTab,
    pondABWTab,
    pondInputTab,
    pondLabTestsTab,
    moonPhase
  },
  data: function() {
    return {
      componentName: "pond-charts",
      pondsObj: {},
      loading: true
    };
  },
  computed: {
    ...mapGetters("user", {
      getUserLocationsObj: "getUserLocationsObj"
    }),
    ...mapGetters("pondDetails", {
      getPondIdPondMap: "getPondIdPondMap",
      pondId: "getSelectedPondId",
      getCultureName: "getCultureName",
      getSelectedPondWithDeviceDetails: "getSelectedPondWithDeviceDetails"
    }),
    selectedPondDetails() {
      return this.PondsObj[this.pondId];
    },
    PondsObj() {
      return this.$store.getters["pond/getPondsObj"];
    },
    location() {
      return this.$store.getters["user/getCurrUserLocation"];
    },
    locationId() {
      return this.location._id;
    },
    computedComponentName: function() {
      return this.$route.query.tab;
    },
    fitlerTabItems: function() {
      return this.itemsUserCanView(this.tabLabels);
    },
    tabLabels() {
      return {
        pondCharts: {
          label: this.$t("Comn_dashboard"),
          ...tabToPermission.pondCharts
        },
        pondFeedTab: {
          label: this.$t("Comn_feed"),
          ...tabToPermission.pondFeedTab
        },
        pondInputTab: {
          label: this.$t("Comn_inputs"),
          ...tabToPermission.pondInputTab
        },
        pondABWTab: {
          label: this.$t("ABW_abw"),
          ...tabToPermission.pondABWTab
        },
        pondLabTestsTab: {
          label: this.$t("LabTst_lab_tests"),
          ...tabToPermission.pondLabTestsTab
        }
      };
    }
  },
  watch: {
    pondId: function(newPondId, oldPondId) {
      this.changeUrl(newPondId);
    }
  },
  beforeCreate() {
    const storeModules = {
      pondDetails: pondDetailsMod,
      pondmother: pondmotherMod,
      resource: resourceMod,
      medicines: medicinesMod,
      labTests: labTestsMod
    };
    for (const key in storeModules) {
      if (!this.$store.hasModule(key)) {
        this.$store.registerModule(
          key,
          this.$lodash.cloneDeep(storeModules[key])
        );
      }
    }
    const viewPermissions = this.$store.getters["user/getViewPermissions"];
    const userType = this.$store.getters["user/getCurrUserType"];
    const tabName = this.$route.query.tab;
    if (!isValidTabName(tabName, tabToPermission, viewPermissions, userType)) {
      this.$router.push({
        path: "/user/pond-details",
        query: {
          tab: getFirstTabBasedOnPermissions(
            tabToPermission,
            viewPermissions,
            userType
          )
        }
      });
    }
  },
  async mounted() {
    await this.initComponent("INIT");
  },
  beforeRouteLeave(to, from, next) {
    const storeModules = {
      pondDetails: pondDetailsMod,
      pondmother: pondmotherMod,
      resource: resourceMod,
      medicines: medicinesMod,
      labTests: labTestsMod
    };
    for (const key in storeModules) {
      if (this.$store.hasModule(key)) {
        this.$store.unregisterModule(key);
      }
    }
    next();
  },
  methods: {
    ...mapActions("pondDetails", {
      changeSelectedPondId: "changeSelectedPondId",
      changePondWithDeviceDetails: "changePondWithDeviceDetails",
      changeSelectedTab: "changeSelectedTab"
    }),
    changeUrl(pondId) {
      if (
        typeof pondId !== "undefined" &&
        pondId !== this.$route.query.pondId &&
        pondId !== ""
      ) {
        this.$router.push({
          path: `/user/pond-details`,
          query: { tab: this.computedComponentName, pondId: pondId }
        });
      }
    },
    async initComponent(event) {
      try {
        this.loading = true;
        await this.$store.dispatch("pond/fetchAllPonds", {
          location_id: this.location._id,
          status: ["ACTIVE"],
          get_all: true,
          order_by: "title"
        });
        this.pondsObj = this.PondsObj;
        let pondId = this.$route.query.pondId;
        const doRouteParamHasPondId = this.$route.query.pondId !== undefined;
        const doLocationHasPondIdInRouteParams =
          doRouteParamHasPondId && this.PondsObj[pondId];
        pondId = doLocationHasPondIdInRouteParams
          ? pondId
          : Object.keys(this.pondsObj)[0];
        if (pondId) {
          await this.changeSelectedPondId(pondId);
        }
      } catch (err) {
        this.errorMessages(err, true);
      } finally {
        this.loading = false;
      }
    },
    async handleTabClick(tabVnode) {
      this.$router.push({
        path: "/user/pond-details",
        query: { tab: tabVnode.paneName, pondId: this.pondId }
      });
      await this.changeSelectedTab(tabVnode.paneName);
    }
  }
};
</script>

<style lang="scss">
.pond-details {
  height: 100%;
  overflow: hidden;

  //   .er-card {
  //     border-radius: 0px;
  // }

  .pond__details__tabs {
    @include responsiveProperty(
      font-size,
      $app_font_size_1,
      $app_font_size_2,
      $app_font_size_3
    );

    display: flex;
  }
  .no-data {
    height: 34rem;
  }
  .card-pond-charts {
    .el-card__body {
      padding: 5px;
    }
  }
  .pond-details-scroll {
    height: 100%;
  }
  .pond-details-scroll-view {
    padding-right: 10px;
  }
  .pond-details-scroll-wrap {
    height: 100%;
    overflow-x: auto;
  }
  .er-tabs {
    .el-tabs__item {
      // width: 80px;
      text-align: center;
      // padding-left: 10px;
      // padding: 0px 0px 0px 0px;
    }
    #tab-labTests {
      text-align: right;
    }
    #tab-abw {
      padding-right: 0px;
    }
    #tab-pond-charts {
      padding-left: 0px;
    }
    .el-tabs__content {
      background-color: #ecf3f9;
      padding: 0px;
    }

    .el-tabs__header {
      padding-bottom: 20px;
    }
    .el-tabs__nav-scroll {
      width: max-content;
      border-radius: 3px;
    }
    .el-tabs__nav-wrap::after {
      width: 0%;
    }
  }
  .el-tabs--bottom .el-tabs__item.is-bottom:last-child,
  .el-tabs--bottom .el-tabs__item.is-top:last-child,
  .el-tabs--top .el-tabs__item.is-bottom:last-child,
  .el-tabs--top .el-tabs__item.is-top:last-child {
    padding-right: 20px;
  }
}
</style>
